// Page text for the local reader, worked out entirely in the browser.
//   • Digital PDFs (website printouts, e-invoices): the PDF's own text layer.
//   • Scans and photos: Tesseract OCR with the Greek + English models in /public/tessdata.
// Both libraries are loaded only when Bulk Import actually needs them, and every file they
// need (workers, WebAssembly, language data) is served from this app's /public folder —
// keep those copies in step with the versions pinned in package.json.

const DPI = 150;                 // what the document model was trained on
const MAX_SIDE = 2600;           // keep huge scans from exhausting memory
const BASE = process.env.PUBLIC_URL || '';

let pdfjsPromise = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((m) => {
      const lib = m.getDocument ? m : m.default;
      lib.GlobalWorkerOptions.workerSrc = `${BASE}/pdfjs/pdf.worker.min.js`;
      return lib;
    });
  }
  return pdfjsPromise;
};

let modelPromise = null;
export const loadModel = () => {
  if (!modelPromise) {
    modelPromise = fetch(`${BASE}/models/doc-classifier.json`, { cache: 'no-cache' })
      .then((r) => { if (!r.ok) throw new Error('Document model not found'); return r.json(); })
      .catch((e) => { modelPromise = null; throw e; });
  }
  return modelPromise;
};

// ---- Text layer -------------------------------------------------------------------
async function hasImages(page, OPS) {
  const ops = await page.getOperatorList();
  return ops.fnArray.some((fn) => fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject || fn === OPS.paintInlineImageXObject);
}

async function textLayer(page) {
  const content = await page.getTextContent();
  const items = content.items.filter((it) => it.str && it.str.trim());
  if (!items.length) return '';
  items.sort((a, b) => (b.transform[5] - a.transform[5]) || (a.transform[4] - b.transform[4]));
  const lines = [];
  let cur = [], y = null;
  for (const it of items) {
    const iy = it.transform[5];
    if (y !== null && Math.abs(iy - y) > 3) { lines.push(cur); cur = []; }
    cur.push(it); y = iy;
  }
  lines.push(cur);
  return lines.map((l) => l.sort((a, b) => a.transform[4] - b.transform[4]).map((i) => i.str).join(' ')).join('\n');
}

// ---- Rendering + OCR ----------------------------------------------------------------
async function render(page) {
  let scale = DPI / 72;
  const vp0 = page.getViewport({ scale });
  const longest = Math.max(vp0.width, vp0.height);
  if (longest > MAX_SIDE) scale *= MAX_SIDE / longest;
  const vp = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(vp.width); canvas.height = Math.round(vp.height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return canvas;
}

const rotate = (src, deg) => {
  const c = document.createElement('canvas');
  const quarter = deg % 180 !== 0;
  c.width = quarter ? src.height : src.width; c.height = quarter ? src.width : src.height;
  const ctx = c.getContext('2d');
  ctx.translate(c.width / 2, c.height / 2); ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
};
const free = (c) => { if (c) { c.width = 0; c.height = 0; } };

async function makeScheduler(n) {
  const { createScheduler, createWorker } = await import('tesseract.js');
  const scheduler = createScheduler();
  for (let i = 0; i < n; i++) {
    // Language files are served from this app (/public/tessdata) so the exact models the
    // classifier was trained against are always used.
    const w = await createWorker('ell+eng', 1, {
      langPath: `${BASE}/tessdata`, gzip: true,
      workerPath: `${BASE}/tesseract/worker.min.js`, corePath: `${BASE}/tesseract/core`, workerBlobURL: false,
    });
    scheduler.addWorker(w);
  }
  return scheduler;
}

async function ocrCanvas(scheduler, canvas) {
  const first = (await scheduler.addJob('recognize', canvas)).data;
  let best = { text: first.text || '', confidence: first.confidence || 0 };
  // Sideways or upside-down scans read as garbage: try the other orientations.
  if (best.confidence < 45 || best.text.replace(/\s/g, '').length < 40) {
    for (const deg of [90, 270, 180]) {
      const r = rotate(canvas, deg);
      const d = (await scheduler.addJob('recognize', r)).data;
      free(r);
      if ((d.confidence || 0) > best.confidence + 15) best = { text: d.text || '', confidence: d.confidence };
      if (best.confidence >= 70) break;
    }
  }
  return best;
}

/**
 * files: [{ idx, bytes, pages }]
 * Returns { [idx]: [{ page, text, ocrConfidence, source }] }.
 * onPage() is called once per finished page.
 */
export async function readPages(files, onPage) {
  const pdfjs = await loadPdfjs();
  const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
  const n = Math.max(1, Math.min(3, Math.floor(cores / 2)));
  let scheduler = null;
  const getScheduler = async () => { if (!scheduler) scheduler = await makeScheduler(n); return scheduler; };

  const out = {};
  const jobs = [];
  const docs = {};
  for (const f of files) {
    out[f.idx] = [];
    // pdf.js takes ownership of the buffer it is given, so hand it a copy.
    docs[f.idx] = await pdfjs.getDocument({ data: new Uint8Array(f.bytes.slice(0)), isEvalSupported: false }).promise;
    for (let p = 1; p <= f.pages; p++) jobs.push({ idx: f.idx, page: p });
  }

  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++];
      let result = { page: job.page, text: '', ocrConfidence: 0, source: 'none' };
      try {
        const page = await docs[job.idx].getPage(job.page);
        const layer = await textLayer(page);
        if (layer.replace(/\s/g, '').length >= 100 && !(await hasImages(page, pdfjs.OPS))) {
          result = { page: job.page, text: layer, ocrConfidence: 95, source: 'text' };
        } else {
          const canvas = await render(page);
          const s = await getScheduler();
          const r = await ocrCanvas(s, canvas);
          free(canvas);
          result = { page: job.page, text: r.text, ocrConfidence: Math.round(r.confidence), source: 'ocr' };
        }
        page.cleanup();
      } catch (err) {
        result.error = err.message;
      }
      out[job.idx].push(result);
      onPage?.(result);
    }
  };
  try {
    await Promise.all(Array.from({ length: n + 1 }, worker));   // one extra so rendering overlaps OCR
  } finally {
    for (const d of Object.values(docs)) d.destroy?.();
    if (scheduler) await scheduler.terminate();
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => a.page - b.page);
  return out;
}
